import { FC } from "react";
import { useTranslation } from "react-i18next";
import BackgroundWrapper from "./BackgroundWrapper";
import { LoadingScreenProps } from "@/types";

const LoadingScreen: FC<LoadingScreenProps> = ({
  showSpinner = true,
  showWarning = true,
  warningMessage,
  progress,
  className = "",
  contained = false,
}) => {
  const { t } = useTranslation('common');
  const resolvedWarningMessage = warningMessage ?? t('loading.defaultWarning');
  const hasProgress = typeof progress === 'number';
  const clampedProgress = hasProgress ? Math.min(100, Math.max(0, progress)) : 0;

  const content = (
    <div
      className={`relative flex items-center justify-center w-full ${contained ? 'min-h-[280px] py-10' : 'min-h-screen'} ${className}`}
    >
      {/* Ambient gold orbs */}
      <div className="ls-orb-gold" aria-hidden="true" />
      <div className="ls-orb-amber" aria-hidden="true" />

      {/* Glass card */}
      <div className="relative z-10 w-[min(92vw,28rem)] rounded-2xl border border-white/5 bg-gradient-to-b from-white/[0.04] to-white/[0.01] px-8 py-10 text-center shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-xl">
        {/* Spinner: stacked rotating rings + glowing core */}
        {showSpinner && (
          <div
            className="relative mx-auto mb-7 h-28 w-28"
            role="status"
            aria-label={t('loading.loadingAriaLabel')}
          >
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full ls-ring-conic animate-[ls-spin-slow_2.4s_linear_infinite]"
              aria-hidden="true"
            />
            {/* Inner counter-rotating ring */}
            <div
              className="absolute inset-3 rounded-full ls-ring-conic-inner animate-[ls-spin-reverse_1.6s_linear_infinite]"
              aria-hidden="true"
            />
            {/* Glowing core */}
            <div
              className="absolute inset-0 m-auto h-8 w-8 rounded-full bg-gradient-to-br from-yellow-200 via-amber-400 to-yellow-600 animate-[ls-core-pulse_2s_ease-in-out_infinite]"
              aria-hidden="true"
            />
            {/* Floating coins */}
            <span
              className="absolute -top-1 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-amber-300/80 shadow-[0_0_8px_rgba(252,211,77,0.8)] animate-[ls-coin-float_3.2s_ease-in-out_infinite]"
              aria-hidden="true"
            />
            <span
              className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-amber-300/80 shadow-[0_0_8px_rgba(252,211,77,0.8)] animate-[ls-coin-float_3.2s_ease-in-out_1.6s_infinite]"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Title */}
        <h2 className="bg-gradient-to-b from-white to-fp-text2 bg-clip-text text-xl font-semibold tracking-tight text-transparent">
          {t('loading.pleaseWait')}
        </h2>

        {/* Loading dots */}
        <div className="mt-3 flex items-center justify-center gap-1.5" aria-hidden="true">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400/70 animate-bounce [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400/70 animate-bounce [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400/70 animate-bounce" />
        </div>

        {/* Progress bar */}
        {hasProgress && (
          <div className="mt-6">
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06] ring-1 ring-inset ring-white/5">
              <div
                className="h-full rounded-full ls-progress-fill transition-[width] duration-500 ease-out"
                style={{ width: `${clampedProgress}%` }}
                role="progressbar"
                aria-valuenow={clampedProgress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('loading.loadingProgressAriaLabel', { percent: clampedProgress })}
              />
            </div>
            <div className="mt-2 text-xs font-medium text-fp-text2 tabular-nums">
              {t('loading.progressComplete', { percent: Math.round(clampedProgress) })}
            </div>
          </div>
        )}

        {/* Warning notice */}
        {showWarning && (
          <div className="mt-7 flex items-start gap-3 rounded-lg border border-amber-500/15 bg-amber-500/[0.06] px-3.5 py-3 text-left">
            <svg
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-300/90">
                {t('loading.important')}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-amber-100/70">
                {resolvedWarningMessage}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (contained) return content;

  return <BackgroundWrapper className="overflow-hidden">{content}</BackgroundWrapper>;
};

export default LoadingScreen;
